import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Film, Layers, ExternalLink } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { formatDate } from '../../lib/utils';

type CutJobStatus =
  | 'PENDING'
  | 'IMPORTING'
  | 'AGENT_RUNNING'
  | 'PUBLISHING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface AiVideoEditJobRow {
  id: string;
  status: CutJobStatus;
  descriptProjectId: string | null;
  descriptEditUrl?: string | null;
  projectUrl?: string | null;
  batchId?: string | null;
  batchLabel?: string | null;
  errorMessage: string | null;
  agentResponse: string | null;
  presets: number[];
  extraPrompt: string | null;
  outputs: unknown;
  submissionId: string | null;
  submissionMediaId: string | null;
  createdAt: string;
  updatedAt: string;
  displayName?: string;
  sourcePreviewUrl?: string | null;
  lastAgentAiCreditsUsed?: number | null;
  submission?: {
    id: string;
    version: number;
    invitation?: {
      job?: { id: string; title: string };
      kolProfile?: { id: string; name: string };
    };
  };
}

function cutJobStatusPill(status: CutJobStatus) {
  switch (status) {
    case 'COMPLETED':
      return <span className="status-pill status-pill-pos">Completed</span>;
    case 'FAILED':
      return <span className="status-pill status-pill-danger">Failed</span>;
    case 'CANCELLED':
      return <span className="status-pill status-pill-muted">Cancelled</span>;
    case 'PENDING':
    case 'IMPORTING':
    case 'AGENT_RUNNING':
    case 'PUBLISHING':
      return <span className="status-pill status-pill-warn">In progress</span>;
    default:
      return <span className="status-pill status-pill-muted">{status}</span>;
  }
}

function Thumbnail({ job }: { job: AiVideoEditJobRow }) {
  const src = job.sourcePreviewUrl;
  if (src && /^https?:\/\//i.test(src)) {
    return (
      <video
        src={src}
        className="w-full h-full object-cover"
        muted
        playsInline
        preload="metadata"
        aria-hidden
      />
    );
  }
  if (src?.startsWith('/')) {
    return (
      <video
        src={src}
        className="w-full h-full object-cover"
        muted
        playsInline
        preload="metadata"
        aria-hidden
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-glass-3 text-glass-text-3">
      <Film className="w-10 h-10 opacity-60" />
    </div>
  );
}

type BatchSummary = {
  batchId: string;
  batchLabel: string | null;
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
};

export default function CreativeCutsPage() {
  const [jobs, setJobs] = useState<AiVideoEditJobRow[]>([]);
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<AiVideoEditJobRow[]>('/ai-video-edit-jobs');
        setJobs(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<BatchSummary[]>('/ai-video-edit-jobs/batches');
        setBatches(res.data);
      } catch {
        setBatches([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ balance: number }>('/agency/ai-credits/me');
        setCreditBalance(res.data.balance);
      } catch {
        setCreditBalance(null);
      }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Scissors className="w-8 h-8 text-accent2" />
            AI Video Edits
          </h1>
          <p className="text-glass-text-2 text-sm mt-1 max-w-2xl">
            Turn long-form clips into short ad cuts (for example 6s, 15s, 30s). Previews and downloads stay in this
            workspace. Each AI edit run uses your agency&apos;s AI edit credits (
            {creditBalance != null ? (
              <Link to="/agency/ai-credits" className="text-accent2 hover:underline">
                balance: {creditBalance}
              </Link>
            ) : (
              <Link to="/agency/ai-credits" className="text-accent2 hover:underline">
                view credits
              </Link>
            )}
            ).
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Link
            to="/agency/ai-video-edits/new?source=upload&bulk=1"
            className="btn-glass inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium border border-glass"
          >
            Bulk upload
          </Link>
          <Link
            to="/agency/ai-video-edits/new?source=upload"
            className="btn-glass inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium border border-glass"
          >
            Upload video
          </Link>
          <Link
            to="/agency/submissions"
            className="btn-accent inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium"
          >
            From submission
          </Link>
        </div>
      </div>

      {batches.length > 0 && (
        <Card className="p-5">
          <CardHeader>
            <CardTitle className="text-glass-text flex items-center gap-2">
              <Layers className="w-5 h-5 text-accent2" />
              Batches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {batches.map((b) => (
                <li key={b.batchId}>
                  <Link
                    to={`/agency/ai-video-edits/batch/${encodeURIComponent(b.batchId)}`}
                    className="block rounded-xl border border-glass bg-glass-2 p-4 hover:-translate-y-0.5 hover:border-accent2/40 hover:bg-glass-3 transition-all duration-150"
                  >
                    <p className="font-medium text-glass-text truncate">
                      {b.batchLabel || 'Batch AI video edits'}
                    </p>
                    <p className="text-xs text-glass-text-3 mt-1">
                      {b.total} video{b.total === 1 ? '' : 's'} · {b.completed} done
                      {b.inProgress > 0 ? ` · ${b.inProgress} in progress` : ''}
                      {b.failed > 0 ? ` · ${b.failed} failed` : ''}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card className="p-5">
        <CardHeader>
          <CardTitle className="text-glass-text">Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-glass-text-2 py-8 text-center">Loading…</p>
          ) : jobs.length === 0 ? (
            <p className="text-glass-text-2 py-8 text-center">
              No jobs yet. Upload a video or start from a submission.
            </p>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {jobs.map((j) => (
                <li key={j.id}>
                  <Link
                    to={`/agency/ai-video-edits/new?jobId=${encodeURIComponent(j.id)}`}
                    className="group block rounded-xl border border-glass bg-glass-2 overflow-hidden hover:-translate-y-0.5 hover:border-accent2/40 hover:bg-glass-3 transition-all duration-150"
                  >
                    <div className="aspect-video bg-glass-3 relative">
                      <Thumbnail job={j} />
                      <div className="absolute top-2 left-2">{cutJobStatusPill(j.status)}</div>
                    </div>
                    <div className="p-3 min-w-0">
                      <p className="font-medium text-glass-text truncate group-hover:text-accent2 transition-colors">
                        {j.displayName ||
                          (j.submission
                            ? `${j.submission.invitation?.job?.title ?? 'Campaign'} · ${j.submission.invitation?.kolProfile?.name ?? 'KOL'}`
                            : 'AI video edit')}
                      </p>
                      <p className="text-xs text-glass-text-3 mt-0.5">
                        {j.presets?.length ? `${j.presets.join('s, ')}s` : '—'} · {formatDate(j.createdAt)}
                      </p>
                      {j.lastAgentAiCreditsUsed != null && (
                        <p className="text-xs text-glass-text-3 mt-1">
                          Last AI units reported: {j.lastAgentAiCreditsUsed}
                        </p>
                      )}
                      {j.errorMessage && (
                        <p className="text-xs text-red-400 mt-1 line-clamp-2">{j.errorMessage}</p>
                      )}
                      {j.descriptEditUrl && (
                        <a
                          href={j.descriptEditUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-accent2 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open in AI Editor
                        </a>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
